import { availableCountryFlags, countryFlagUrl, countryName } from "./countries";

type ImageGlob = (
  pattern: string | string[],
  options: { eager: true; query: "?url"; import: "default" },
) => Record<string, string>;

// Vite expands `import.meta.glob` only where it is written out, with literal patterns.

/**
 * The bundled image library: images a dashboard can show beside its title or a
 * page's title (a pictogram, a scope map, a flag, a client or partner logo).
 * Each collection is one folder under `assets/`; adding a file adds an image,
 * with no TypeScript registration. `import.meta.glob` needs literal patterns, so
 * a new collection adds one entry below. See `assets/scope-maps/README.md`.
 */
const files = (found: Record<string, string>) =>
  Object.entries(found).map(([path, url]) => ({ code: fileCode(path), url }));

const COLLECTIONS = [
  {
    category: "icon",
    label: "Icons",
    // Line pictograms: drawn in the surrounding (or accent) color, like text.
    tint: true,
    entries: () =>
      files(
        (import.meta as ImportMeta & { glob: ImageGlob }).glob("../../assets/icons/*.svg", {
          eager: true,
          query: "?url",
          import: "default",
        }),
      ),
  },
  {
    category: "map",
    label: "Maps",
    // Monochrome silhouettes: drawn in the surrounding text color, in both themes.
    tint: true,
    entries: () =>
      files(
        (import.meta as ImportMeta & { glob: ImageGlob }).glob("../../assets/scope-maps/*.svg", {
          eager: true,
          query: "?url",
          import: "default",
        }),
      ),
  },
  {
    category: "logo",
    label: "Logos",
    // Brand artwork keeps its own colors.
    tint: false,
    entries: () =>
      files(
        (import.meta as ImportMeta & { glob: ImageGlob }).glob(
          ["../../assets/logos/*.svg", "../../assets/logos/*.png", "../../assets/logos/*.webp"],
          { eager: true, query: "?url", import: "default" },
        ),
      ),
  },
  {
    category: "flag",
    label: "Flags",
    // The country flag catalog (`assets/country-flags`), named by country.
    tint: false,
    entries: () =>
      availableCountryFlags().map((flag) => ({
        code: flag.toLowerCase(),
        url: countryFlagUrl(flag)!,
        label: countryName(flag),
      })),
  },
] as const;

export type LibraryImageCategory = (typeof COLLECTIONS)[number]["category"];

export interface LibraryImageAsset {
  /** Stable code a dashboard stores: the file name without its extension. */
  code: string;
  label: string;
  category: LibraryImageCategory;
  categoryLabel: string;
  url: string;
  /** Drawn as a silhouette in the current text color rather than with its own colors. */
  tint: boolean;
}

/** Codes are stored by the API, which accepts lowercase kebab-case only. */
const CODE = /^[a-z][a-z0-9-]{0,63}$/;

const fileCode = (path: string) => path.slice(path.lastIndexOf("/") + 1).replace(/\.[^.]+$/, "");
const fileLabel = (code: string) =>
  code.replace(/-/g, " ").replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const images: LibraryImageAsset[] = [];
for (const collection of COLLECTIONS) {
  const entries = (collection.entries() as { code: string; url: string; label?: string }[])
    .filter((entry) => entry.url)
    .map((entry) => ({ ...entry, label: entry.label || fileLabel(entry.code) }))
    .sort((first, second) => first.label.localeCompare(second.label));
  for (const { code, url, label } of entries) {
    // One code names one image across the whole library: the first collection keeps it.
    if (!CODE.test(code) || images.some((image) => image.code === code)) continue;
    images.push({
      code,
      label,
      category: collection.category,
      categoryLabel: collection.label,
      url,
      tint: collection.tint,
    });
  }
}

/** Every bundled image, grouped by collection, alphabetical within it. */
export function availableLibraryImages(): readonly LibraryImageAsset[] {
  return images;
}

export function findLibraryImage(code: string | undefined): LibraryImageAsset | undefined {
  return code ? images.find((image) => image.code === code) : undefined;
}
